/* ==========================================================================
   BNB mobile sticky add-to-cart bar
   - Shows/hides based on main button visibility.
   - Syncs variant name and price.
   - Updates total price when quantity changes.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('bnb-sticky-atc')) return;

  const MOBILE_QUERY = '(max-width: 749.98px)';

  class BnbStickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-main-product') || document;

      this.button = this.querySelector('[data-bnb-sticky-add]');
      this.buttonText = this.querySelector('[data-bnb-sticky-button-text]');
      this.priceSlot = this.querySelector('[data-bnb-sticky-price]');
      this.variantName = this.querySelector('[data-bnb-sticky-variant]');

      // Current variant data (stored for price calculations)
      this.currentVariant = null;
      this.quantity = 1;

      if (!this.getMainButton() || !this.button) return;

      this.mql = window.matchMedia(MOBILE_QUERY);
      this.mainButtonVisible = true;

      this.onAdd = this.onAdd.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.updateVisibility = this.updateVisibility.bind(this);
      this.updatePrice = this.updatePrice.bind(this);
      this.updateButtonContent = this.updateButtonContent.bind(this);

      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '0px',
        threshold: 0
      });
      this.observeMainButton();

      this.button.addEventListener('click', this.onAdd);
      document.addEventListener('on:variant:change', this.onVariantChange);

      // Quantity input listener
      this.attachQuantityListener();

      // Re‑evaluate on breakpoint change
      if (typeof this.mql.addEventListener === 'function') {
        this.mql.addEventListener('change', this.updateVisibility);
      } else if (typeof this.mql.addListener === 'function') {
        this.mql.addListener(this.updateVisibility);
      }

      // Initial price update (ensures quantity is applied)
      this.updatePrice();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('on:variant:change', this.onVariantChange);
      if (this.mql) {
        if (typeof this.mql.removeEventListener === 'function') {
          this.mql.removeEventListener('change', this.updateVisibility);
        } else if (typeof this.mql.removeListener === 'function') {
          this.mql.removeListener(this.updateVisibility);
        }
      }
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('change', this.onQuantityChange);
        this.quantityInput.removeEventListener('input', this.onQuantityChange);
      }
      document.body.classList.remove('bnb-sticky-atc-visible');
    }

    /** Get the current main add‑to‑cart button (re‑queried after replacements). */
    getMainButton() {
      return (
        this.section.querySelector('.quantity-submit-row .add-to-cart') ||
        this.section.querySelector('button[name="add"]')
      );
    }

    /** (Re)point the IntersectionObserver at the current main button. */
    observeMainButton() {
      const current = this.getMainButton();
      if (!current || current === this.mainButton) return;
      if (this.mainButton) this.observer.unobserve(this.mainButton);
      this.mainButton = current;
      this.observer.observe(this.mainButton);
    }

    /** Listen to quantity changes on the main product form. */
    attachQuantityListener() {
      const input = this.section.querySelector('input[name="quantity"]');
      if (input) {
        this.quantityInput = input;
        this.quantityInput.addEventListener('change', this.onQuantityChange);
        this.quantityInput.addEventListener('input', this.onQuantityChange);
        this.quantity = parseInt(this.quantityInput.value, 10) || 1;
      }
    }

    /** Update stored quantity and refresh price. */
    onQuantityChange() {
      if (this.quantityInput) {
        const val = parseInt(this.quantityInput.value, 10);
        if (!isNaN(val) && val > 0) {
          this.quantity = val;
        }
      }
      this.updatePrice();
    }

    /** Mirror the theme's price markup and then apply quantity multiplication. */
    updatePrice() {
      if (!this.priceSlot) return;
      // If we don't have a current variant yet, try to get one from the main price.
      if (!this.currentVariant) {
        // Attempt to extract price from existing price HTML (fallback)
        const mainPrice = this.section.querySelector('.product-info__price');
        if (mainPrice) {
          const priceText = mainPrice.textContent.trim();
          const match = priceText.match(/([\d,]+\.\d{2})/);
          if (match) {
            const num = parseFloat(match[1].replace(/,/g, ''));
            if (!isNaN(num)) {
              this.currentVariant = { price: Math.round(num * 100) };
            }
          }
        }
        if (!this.currentVariant) return;
      }

      const priceCents = this.currentVariant.price;
      const totalCents = priceCents * this.quantity;
      const formatted = this.formatMoney(totalCents);

      this.priceSlot.innerHTML = `<span class="price price--without-sale">${formatted}</span>`;
    }

    /** Format money using Shopify's formatMoney or a fallback. */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents / 100, window.theme?.moneyFormat || '${{amount}}');
      }

      const currency = window.theme?.currency || 'USD';
      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      });
      return formatter.format(cents / 100);
    }

    /** Rebuild the button content: price + "Add to cart" when available, else "Sold out". */
    updateButtonContent(available) {
      if (available) {
        const addText = this.dataset.addText || 'Add to cart';
        // Build HTML with price container + text
        this.buttonText.innerHTML = `
          <div class="bnb-sticky-atc__price" data-bnb-sticky-price></div>
          ${addText}
        `;
        // Re‑query the price slot (now inside the button)
        this.priceSlot = this.querySelector('[data-bnb-sticky-price]');
        // Update the price with current quantity and variant
        this.updatePrice();
      } else {
        this.buttonText.textContent = this.dataset.soldoutText || 'Sold out';
        // Price slot no longer exists
        this.priceSlot = null;
      }
    }

    /** Intersection observer callback. */
    onIntersect(entries) {
      this.mainButtonVisible = entries[0].isIntersecting;
      this.updateVisibility();
    }

    /** Show/hide bar based on main button visibility and mobile breakpoint. */
    updateVisibility() {
      const show = !this.mainButtonVisible && this.mql.matches;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Toggle body class for conditional widget offsets
      document.body.classList.toggle('bnb-sticky-atc-visible', show);
    }

    /** Handle variant change from the theme. */
    onVariantChange(evt) {
      if (evt.target && this.section.contains && !this.section.contains(evt.target)) return;

      const variant = evt.detail && evt.detail.variant;
      if (!variant) return;

      // Update variant name
      if (this.variantName) {
        this.variantName.textContent = variant.title || '';
      }

      // Store variant price (in cents)
      this.currentVariant = {
        price: variant.price,
      };

      const available = !!(variant && variant.available);
      this.button.disabled = !available;

      // Rebuild the button content (with or without price)
      this.updateButtonContent(available);

      // Re‑observe the (potentially replaced) main button and refresh quantity listener
      requestAnimationFrame(() => {
        this.observeMainButton();
        this.attachQuantityListener();
      });
    }

    /** Handle add‑to‑cart click – submits the main form. */
    onAdd() {
      if (this.button.disabled) return;
      const form = this.section.querySelector('.js-product-form');
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else if (form) {
        form.submit();
      } else if (this.mainButton) {
        this.mainButton.click();
      }
    }
  }

  customElements.define('bnb-sticky-atc', BnbStickyAtc);
})();